/**
 * All sound is synthesised at runtime, so the prototype ships with no audio
 * files. Zone ambiences are looping beds whose gains are set from the
 * player's distance to each zone sphere; one base bed always plays.
 */
export type AmbienceKind = 'base' | 'fire' | 'forest' | 'workshop' | 'wind' | 'water' | 'crowd' | 'forge' | 'tavern' | 'city' | 'arena';

export class Audio {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private sfx!: GainNode;
  private beds = new Map<AmbienceKind, GainNode>();
  private muted = false;
  private noise!: AudioBuffer;
  private lastStep = 0;
  private musicTimer = 0;
  private musicBus: GainNode | null = null;

  get ready(): boolean {
    return !!this.ctx;
  }

  /** Must be called from a user gesture (the Begin button). */
  start(): void {
    if (this.ctx) return;
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = 0;
    this.master.connect(ctx.destination);
    this.master.gain.setTargetAtTime(0.9, ctx.currentTime, 1.0);
    this.sfx = ctx.createGain();
    this.sfx.gain.value = 0.7;
    this.sfx.connect(this.master);

    const len = ctx.sampleRate * 4;
    this.noise = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = this.noise.getChannelData(0);
    let b = 0;
    for (let i = 0; i < len; i++) {
      b = (b + 0.02 * (Math.random() * 2 - 1)) / 1.02; // brownish
      data[i] = b * 3.5;
    }

    this.makeBed('base', 0.22, (out) => this.noiseBed(out, 'lowpass', 380, 0.35, 0.07));
    this.makeBed('wind', 0, (out) => this.noiseBed(out, 'bandpass', 700, 0.8, 0.15));
    this.makeBed('water', 0, (out) => this.noiseBed(out, 'lowpass', 900, 0.5, 0.6));
    this.makeBed('fire', 0, (out) => {
      this.noiseBed(out, 'lowpass', 220, 0.9, 0.2);
      this.crackle(out);
    });
    this.makeBed('forest', 0, (out) => {
      this.noiseBed(out, 'highpass', 2600, 0.12, 0.25);
      this.birds(out);
    });
    this.makeBed('workshop', 0, (out) => {
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.value = 55;
      const g = ctx.createGain();
      g.gain.value = 0.05;
      o.connect(g).connect(out);
      o.start();
      this.clinks(out);
    });
    this.makeBed('crowd', 0, (out) => {
      // Babble: several voice-band noise layers with fast random swells.
      for (let i = 0; i < 4; i++) this.noiseBed(out, 'bandpass', 380 + i * 260, 2.2, 0.2);
      this.murmur(out);
    });
    this.makeBed('forge', 0, (out) => {
      this.noiseBed(out, 'lowpass', 160, 1.2, 0.25);
      this.hammer(out);
    });
    this.makeBed('tavern', 0, (out) => {
      this.noiseBed(out, 'bandpass', 600, 1.5, 0.15);
      this.jig(out);
    });
    this.makeBed('city', 0, (out) => {
      // Traffic rumble, a mains hum, and the odd car horn.
      this.noiseBed(out, 'lowpass', 140, 0.7, 0.35);
      const o = this.ctx!.createOscillator();
      o.frequency.value = 60;
      const g = this.ctx!.createGain();
      g.gain.value = 0.03;
      o.connect(g).connect(out);
      o.start();
      const honk = () => {
        if (!this.ctx) return;
        const f = 350 + Math.random() * 120;
        this.tone(f, f, 0.22, 0.05, 'square', out);
        this.tone(f * 1.26, f * 1.26, 0.22, 0.04, 'square', out);
        setTimeout(honk, 3000 + Math.random() * 6000);
      };
      honk();
    });
    this.makeBed('arena', 0, (out) => {
      // Ominous low pulse at BigGame HQ.
      const o = this.ctx!.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = 41;
      const f = this.ctx!.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = 220;
      const g = this.ctx!.createGain();
      g.gain.value = 0.12;
      const lfo = this.ctx!.createOscillator();
      lfo.frequency.value = 0.9;
      const lg = this.ctx!.createGain();
      lg.gain.value = 0.08;
      lfo.connect(lg).connect(g.gain);
      o.connect(f).connect(g).connect(out);
      o.start();
      lfo.start();
    });
    this.music();
  }

  /** The dragon, somewhere below. Never seen, definitely heard. */
  roar(): void {
    if (!this.ctx) return;
    this.burst({ freq: 420, q: 0.7, dur: 2.6, gain: 0.9, type: 'lowpass', sweep: 60 });
    this.tone(110, 46, 2.2, 0.45, 'sawtooth');
    this.tone(82, 38, 2.6, 0.35, 'sawtooth', undefined, 0.15);
  }

  slash(): void {
    this.burst({ freq: 2400, q: 0.7, dur: 0.16, gain: 0.35, type: 'bandpass', sweep: 700 });
  }

  zap(): void {
    this.tone(1400, 220, 0.25, 0.12, 'sawtooth');
  }

  hurt(): void {
    this.tone(300, 120, 0.25, 0.25, 'square');
    this.burst({ freq: 500, q: 1, dur: 0.15, gain: 0.3, type: 'lowpass' });
  }

  boom(): void {
    this.burst({ freq: 180, q: 0.6, dur: 0.9, gain: 0.8, type: 'lowpass', sweep: 40 });
    this.tone(90, 30, 0.6, 0.35, 'sine');
  }

  alarm(): void {
    [880, 660, 880].forEach((f, i) => this.tone(f, f, 0.14, 0.08, 'square', undefined, i * 0.16));
  }

  coin(): void {
    this.tone(1320, 1320, 0.08, 0.12, 'square');
    this.tone(1760, 1760, 0.18, 0.1, 'square', undefined, 0.07);
  }

  questStart(): void {
    [523, 659, 784].forEach((f, i) => this.tone(f, f, 0.2, 0.1, 'triangle', undefined, i * 0.09));
  }

  questDone(): void {
    [523, 659, 784, 1047].forEach((f, i) => this.tone(f, f * 1.01, 0.32, 0.12, 'triangle', undefined, i * 0.11));
  }

  pickup(): void {
    this.tone(880, 1760, 0.15, 0.1, 'sine');
  }

  hit(): void {
    this.burst({ freq: 260, q: 1, dur: 0.18, gain: 0.5, type: 'lowpass' });
    this.tone(180, 90, 0.12, 0.2, 'sine');
  }

  deny(): void {
    this.tone(220, 160, 0.18, 0.12, 'square');
  }

  setMuted(m: boolean): void {
    this.muted = m;
    if (this.ctx) this.master.gain.setTargetAtTime(m ? 0 : 0.9, this.ctx.currentTime, 0.3);
  }

  get isMuted(): boolean {
    return this.muted;
  }

  /** Weight in 0..1 per ambience; called every frame. */
  setAmbience(weights: Partial<Record<AmbienceKind, number>>): void {
    if (!this.ctx) return;
    for (const [kind, gain] of this.beds) {
      if (kind === 'base') continue;
      const target = (weights[kind] ?? 0) * 0.55;
      gain.gain.setTargetAtTime(target, this.ctx.currentTime, 0.4);
    }
  }

  footstep(speed: number): void {
    if (!this.ctx || speed < 0.5) return;
    const now = this.ctx.currentTime;
    const interval = speed > 4 ? 0.26 : 0.36;
    if (now - this.lastStep < interval) return;
    this.lastStep = now;
    this.burst({ freq: 900 + Math.random() * 400, q: 1.2, dur: 0.07, gain: 0.18, type: 'bandpass' });
  }

  jump(): void {
    this.tone(330, 520, 0.12, 0.12, 'triangle');
  }

  land(): void {
    this.burst({ freq: 300, q: 0.8, dur: 0.12, gain: 0.3, type: 'lowpass' });
  }

  blip(pitch = 1): void {
    this.tone(520 * pitch, 560 * pitch, 0.045, 0.06, 'square');
  }

  pop(): void {
    this.tone(620, 980, 0.09, 0.12, 'sine');
  }

  whoosh(): void {
    this.burst({ freq: 1200, q: 0.6, dur: 0.9, gain: 0.35, type: 'bandpass', sweep: 300 });
  }

  // --- building blocks ---

  private makeBed(kind: AmbienceKind, level: number, build: (out: GainNode) => void): void {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    g.gain.value = level;
    g.connect(this.master);
    build(g);
    this.beds.set(kind, g);
  }

  private noiseBed(out: AudioNode, type: BiquadFilterType, freq: number, q: number, level: number): void {
    const ctx = this.ctx!;
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    src.playbackRate.value = 0.6 + Math.random() * 0.5;
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = q;
    const g = ctx.createGain();
    g.gain.value = level;
    // Slow swell so loops don't sound static.
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.07 + Math.random() * 0.05;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = level * 0.5;
    lfo.connect(lfoGain).connect(g.gain);
    src.connect(f).connect(g).connect(out);
    src.start(0, Math.random() * 3);
    lfo.start();
  }

  private crackle(out: AudioNode): void {
    const tick = () => {
      if (!this.ctx) return;
      for (let i = 0; i < 1 + Math.floor(Math.random() * 3); i++) {
        this.burst({ freq: 2000 + Math.random() * 3000, q: 4, dur: 0.02 + Math.random() * 0.03, gain: 0.25 + Math.random() * 0.4, type: 'bandpass', out, delay: Math.random() * 0.12 });
      }
      setTimeout(tick, 80 + Math.random() * 260);
    };
    tick();
  }

  private birds(out: AudioNode): void {
    const chirp = () => {
      if (!this.ctx) return;
      const base = 2200 + Math.random() * 1600;
      const n = 2 + Math.floor(Math.random() * 4);
      for (let i = 0; i < n; i++) this.tone(base, base * (1.15 + Math.random() * 0.3), 0.06, 0.07, 'sine', out, i * 0.09);
      setTimeout(chirp, 1500 + Math.random() * 4000);
    };
    chirp();
  }

  private clinks(out: AudioNode): void {
    const clink = () => {
      if (!this.ctx) return;
      const f = 1400 + Math.random() * 900;
      this.tone(f, f * 0.98, 0.25, 0.08, 'sine', out);
      setTimeout(clink, 1800 + Math.random() * 3200);
    };
    clink();
  }

  private murmur(out: AudioNode): void {
    // Random shouts and laughs poking out of the crowd.
    const ping = () => {
      if (!this.ctx) return;
      const f = 300 + Math.random() * 400;
      this.tone(f, f * (0.8 + Math.random() * 0.5), 0.25 + Math.random() * 0.3, 0.05, 'sawtooth', out);
      setTimeout(ping, 400 + Math.random() * 1400);
    };
    ping();
  }

  private hammer(out: AudioNode): void {
    const strike = () => {
      if (!this.ctx) return;
      // Clang, clang... pause. Anvil rhythm.
      for (let i = 0; i < 3; i++) {
        const f = 1900 + Math.random() * 300;
        this.tone(f, f * 0.97, 0.35, 0.22, 'triangle', out, i * 0.42);
        this.tone(f * 2.76, f * 2.7, 0.2, 0.08, 'sine', out, i * 0.42);
      }
      setTimeout(strike, 2000 + Math.random() * 1500);
    };
    strike();
  }

  private jig(out: AudioNode): void {
    const notes = [0, 4, 7, 9, 7, 4, 2, 4, 5, 9, 12, 9, 7, 5, 4, 2];
    let i = 0;
    const step = () => {
      if (!this.ctx) return;
      const f = 392 * Math.pow(2, notes[i % notes.length] / 12);
      this.tone(f, f, 0.16, 0.06, 'triangle', out);
      i++;
      setTimeout(step, 180);
    };
    step();
  }

  /** Fade the realm's pad out (Burnwood Studios has its own score) or back in. */
  setMusic(on: boolean): void {
    if (this.ctx && this.musicBus) this.musicBus.gain.setTargetAtTime(on ? 0.05 : 0, this.ctx.currentTime, 0.8);
  }

  private music(): void {
    // A slow pentatonic pad; a new chord every few seconds.
    const ctx = this.ctx!;
    const bus = ctx.createGain();
    bus.gain.value = 0.05;
    this.musicBus = bus;
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.value = 1200;
    bus.connect(f).connect(this.master);
    const scale = [0, 2, 4, 7, 9, 12, 14, 16];
    const root = 196;
    const chord = () => {
      if (!this.ctx) return;
      const now = ctx.currentTime;
      const pick = [0, 2, 4].map(() => scale[Math.floor(Math.random() * scale.length)]);
      for (const step of pick) {
        const o = ctx.createOscillator();
        o.type = 'triangle';
        o.frequency.value = root * Math.pow(2, step / 12);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(0.6, now + 1.6);
        g.gain.linearRampToValueAtTime(0, now + 6.5);
        o.connect(g).connect(bus);
        o.start(now);
        o.stop(now + 6.6);
      }
      this.musicTimer = window.setTimeout(chord, 4200);
    };
    chord();
  }

  private tone(f0: number, f1: number, dur: number, gain: number, type: OscillatorType, out?: AudioNode, delay = 0): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime + delay;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(f1, t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(out ?? this.sfx);
    o.start(t);
    o.stop(t + dur + 0.02);
  }

  private burst(o: { freq: number; q: number; dur: number; gain: number; type: BiquadFilterType; out?: AudioNode; delay?: number; sweep?: number }): void {
    if (!this.ctx) return;
    const ctx = this.ctx;
    const t = ctx.currentTime + (o.delay ?? 0);
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    const f = ctx.createBiquadFilter();
    f.type = o.type;
    f.frequency.setValueAtTime(o.freq, t);
    if (o.sweep) f.frequency.exponentialRampToValueAtTime(o.sweep, t + o.dur);
    f.Q.value = o.q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(o.gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + o.dur);
    src.connect(f).connect(g).connect(o.out ?? this.sfx);
    src.start(t, Math.random() * 3, o.dur + 0.05);
  }

  dispose(): void {
    clearTimeout(this.musicTimer);
    void this.ctx?.close();
    this.ctx = null;
  }
}
